import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { ArrowRight, Instagram, Twitter, Youtube } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-[#FDFBF7]/25 focus:border-[#FF8E9E] outline-none py-4 text-xl md:text-2xl font-light placeholder:text-[#FDFBF7]/30 transition-colors duration-300 text-[#FDFBF7]";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", project_type: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent — we'll scoop back to you soon.");
      setForm({ name: "", email: "", project_type: "", message: "" });
    } catch (err) {
      toast.error("Something melted. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#2D1A13] text-[#FDFBF7] py-28 md:py-44">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-20 lg:gap-32">
        <Reveal>
          <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#FDFBF7]/50 mb-6">Say Hello</p>
          <h2 className="font-serif-display font-bold tracking-tighter text-5xl md:text-7xl leading-[1.02]">
            Let's make you
            <span className="italic text-[#FF8E9E]"> undeniable.</span>
          </h2>
          <p className="mt-8 max-w-md text-base font-light leading-relaxed text-[#FDFBF7]/65">
            Tell us about your project — an EP, a tour, a total reinvention. We reply within two working days, usually with too many ideas.
          </p>
          <div className="mt-14">
            <a
              data-testid="contact-email-link"
              href="mailto:hello@icecreamsocial.studio"
              className="font-serif-display italic text-2xl md:text-3xl text-[#FDFBF7] underline decoration-[#FF8E9E] decoration-2 underline-offset-8 hover:text-[#FF8E9E] transition-colors duration-300"
            >
              hello@icecreamsocial.studio
            </a>
            <div className="mt-10 flex gap-4">
              {[
                { icon: Instagram, id: "instagram" },
                { icon: Twitter, id: "twitter" },
                { icon: Youtube, id: "youtube" },
              ].map(({ icon: Icon, id }) => (
                <a
                  key={id}
                  data-testid={`social-link-${id}`}
                  href="#contact"
                  aria-label={id}
                  className="w-12 h-12 rounded-full border border-[#FDFBF7]/25 flex items-center justify-center hover:bg-[#FF8E9E] hover:border-[#FF8E9E] hover:text-[#2D1A13] transition-colors duration-300"
                >
                  <Icon size={18} strokeWidth={1.6} />
                </a>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form data-testid="contact-form" onSubmit={submit} className="flex flex-col gap-10 lg:pt-24">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.3em] text-[#FDFBF7]/50 mb-1">Your name</label>
              <input
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Frank Ocean"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.3em] text-[#FDFBF7]/50 mb-1">Email</label>
              <input
                data-testid="contact-email-input"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="you@studio.com"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.3em] text-[#FDFBF7]/50 mb-1">What are we making?</label>
              <input
                data-testid="contact-project-input"
                value={form.project_type}
                onChange={set("project_type")}
                placeholder="EP rollout, full rebrand, tour identity…"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.3em] text-[#FDFBF7]/50 mb-1">The details</label>
              <textarea
                data-testid="contact-message-input"
                required
                rows={3}
                value={form.message}
                onChange={set("message")}
                placeholder="Timeline, budget range, dreams…"
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="group mt-4 w-fit rounded-full bg-[#FF8E9E] text-[#2D1A13] text-xs font-bold uppercase tracking-[0.2em] px-10 py-5 flex items-center gap-3 hover:bg-[#FDFBF7] transition-colors duration-300 disabled:opacity-60"
            >
              {sending ? "Scooping…" : "Send it over"}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
